import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  Home as HomeIcon,
  Bed,
  MapPin,
  Quote,
  Star,
  ArrowRight
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import BookingWidget from '../components/BookingWidget';
import axios from 'axios';
import RichText from '../components/RichText';
import UnescoLink from '../components/UnescoLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Clean placeholder - no text overlay
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800"%3E%3Cdefs%3E%3ClinearGradient id="g" x1="0%25" y1="0%25" x2="100%25" y2="100%25"%3E%3Cstop offset="0%25" stop-color="%231E3A5F"/%3E%3Cstop offset="100%25" stop-color="%232d4a6e"/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill="url(%23g)" width="1200" height="800"/%3E%3C/svg%3E';

const Home = () => {
  const { t, language } = useLanguage();
  const [reviews, setReviews] = useState([]);

  const features = [
    { icon: HomeIcon, label: t('features.spaciousVilla'), description: t('features.spaciousVillaDesc'), to: '/villa' },
    { icon: Bed, label: t('features.bedrooms'), description: t('features.bedroomsDesc'), to: '/bedrooms' },
    { icon: MapPin, label: t('features.location'), description: t('features.locationDesc'), to: '/location' },
  ];

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    fetchReviews();
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section - Compact */}
      <section
        data-testid="hero-section"
        className="relative min-h-[75vh] flex items-center justify-center overflow-hidden bg-[#1E3A5F]"
      >
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={PLACEHOLDER_IMAGE}
            alt="Villa Aristotle - Corfu, Greece"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-[#1E3A5F]/40" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pt-20 pb-6">
          <p 
            className="uppercase tracking-[0.3em] text-sm md:text-base mb-3 text-[#D4A72C] opacity-0 animate-fade-up"
            style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}
          >
            {t('hero.subtitle')}
          </p>
          <h1 
            className="font-serif text-5xl md:text-7xl lg:text-8xl font-semibold mb-4 opacity-0 animate-fade-up"
            style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
          >
            <span className="gold-shimmer">Villa Aristotle</span>
          </h1>
          <p 
            className="text-lg md:text-xl text-white mb-2 opacity-0 animate-fade-up"
            style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
          >
            {t('hero.tagline')}
          </p>
          <p 
            className="text-sm md:text-base text-white/70 mb-6 opacity-0 animate-fade-up"
            style={{ animationDelay: '0.35s', animationFillMode: 'forwards' }}
          >
            <RichText text={t('hero.proximity')} tokens={{ unesco: <UnescoLink textKey="hero.unescoLink" className="text-[#D4A72C]" /> }} />
          </p>
          
          {/* Hero Booking Widget */}
          <div 
            className="max-w-4xl mx-auto opacity-0 animate-fade-up"
            style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
          >
            <BookingWidget variant="hero" />
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-5 h-8 rounded-full border-2 border-[#D4A72C]/50 flex items-start justify-center p-1.5">
            <div className="w-1 h-1.5 bg-[#D4A72C] rounded-full" />
          </div>
        </div>
      </section>

      {/* Introduction Section - Compact */}
      <section data-testid="intro-section" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-12 h-0.5 bg-[#D4A72C] mx-auto mb-6" />
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl mb-4 text-[#1E3A5F]">
              {t('sections.welcomeTitle')}
            </h2>
            <p className="text-[#1E3A5F]/70 leading-relaxed">
              {t('sections.welcomeText')}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section - Compact */}
      <section data-testid="features-section" className="py-16 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="uppercase tracking-widest text-xs text-[#D4A72C] mb-2">
              {t('sections.featuresTitle')}
            </p>
            <h2 className="font-serif text-2xl md:text-3xl text-[#1E3A5F]">
              {t('sections.featuresSubtitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.to}
                data-testid={`feature-${index}`}
                className="block text-center group cursor-pointer"
              >
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center group-hover:bg-[#1E3A5F] transition-all duration-300">
                  <feature.icon className="w-6 h-6 text-[#1E3A5F] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-semibold text-[#1E3A5F] mb-1">{feature.label}</h3>
                <p className="text-sm text-[#1E3A5F]/60">{feature.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* Location Preview - Compact */}
      <section data-testid="location-preview" className="py-16 md:py-20 bg-[#1E3A5F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-lg text-white">
            <p className="uppercase tracking-widest text-xs mb-2 text-[#D4A72C]">
              {t('sections.locationTitle')}
            </p>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl mb-4">
              {t('sections.locationSubtitle')}
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              {t('location.aboutCorfuText')}
            </p>
            <Link to="/location">
              <Button
                data-testid="explore-location-btn"
                className="bg-[#D4A72C] text-[#1E3A5F] hover:bg-[#D4A72C]/90 btn-lift font-semibold"
              >
                {t('common.learnMore')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section data-testid="cta-section" className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="w-12 h-0.5 bg-[#D4A72C] mx-auto mb-6" />
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl mb-4 text-[#1E3A5F]">
            {t('sections.ctaTitle')}
          </h2>
          <p className="text-[#1E3A5F]/70 mb-6 max-w-2xl mx-auto">
            {t('sections.ctaText')}
          </p>
          <Link to="/booking">
            <Button
              data-testid="cta-book-now-btn"
              size="lg"
              className="bg-[#1E3A5F] text-white hover:bg-[#1E3A5F]/90 btn-lift px-10 py-5"
            >
              {t('booking.requestBooking')}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Mobile Sticky Booking Widget */}
      <BookingWidget variant="sticky" />
    </div>
  );
};

export default Home;
